import { memo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRightIcon, Inbox } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { AvatarImage } from "@radix-ui/react-avatar";
import { CopyButton } from "@/components/ui/copy-button";
import { Pin } from "@/components/ui/pin";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { UsernameColumnCellProps } from "../types";
import { useCreatorsTableContext } from "../CreatorsTableContext";
import { useInboxStore } from "@/stores/inbox/inboxStore";

function UsernameColumnCell({ creator, selected }: UsernameColumnCellProps) {
  const { selectCreator: onSelectCreator } = useCreatorsTableContext();
  // Boolean selector: the whole-store subscription re-rendered every username
  // cell on ANY inbox store write (thread load resolving mid-panel-tween).
  const inboxLoading = useInboxStore((s) => s.isLoading);
  const {
    _id: creatorId,
    username,
    email,
    platform_id,
    platform,
    avatar,
    next_message: nextMessage,
    thread_id: threadId,
    autopilot,
  } = creator;

  const profileLink =
    platform === "tiktok"
      ? `https://www.tiktok.com/@${platform_id}`
      : platform === "youtube"
      ? `https://www.youtube.com/channel/${platform_id}`
      : `https://www.instagram.com/${username}`;

  return (
    <div className="flex items-center justify-between gap-2 max-w-105 w-auto pr-2">
      <Button
        asChild
        variant="ghost"
        className="group [&_svg]:size-3 relative w-auto flex flex-row items-center justify-between gap-2 h-11 pl-1 pr-2 hover:bg-muted-foreground/15 rounded-md transition-all"
      >
        <Link to={profileLink} target="_blank" rel="noreferrer">
          <Avatar
            className={`size-9.5 transition-all border bg-muted p-0.25 ${
              selected ? "ring-3 ring-primary/30 border-primary" : ""
            }`}
          >
            <AvatarImage src={avatar} alt="Avatar" className="rounded-pill" />
            <AvatarFallback>
              {username?.charAt(0)?.toUpperCase()}
              {username?.charAt(1)?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col items-start justify-center gap-0">
            <span className="max-w-44 truncate font-medium text-sm">@{username}</span>
            <span
              className="max-w-44 font-normal text-muted-foreground text-xs hover:underline underline-offset-2 truncate cursor-copy"
              title="Click to copy email"
              onClick={(e) => {
                navigator.clipboard.writeText(email);
                e.preventDefault();
                toast.success("Email copied to clipboard!");
              }}
            >
              {email}
            </span>
          </div>
          <ArrowUpRightIcon className="shrink-0 absolute top-2 right-2 opacity-10 group-hover:opacity-100 group-hover:translate-x-0.75 group-hover:-translate-y-0.75 transition-all text-foreground" />
        </Link>
      </Button>
      <div className="flex flex-row items-center gap-2 ml-auto">
        <CopyButton
          buttonText=""
          tooltipText="Copy Campaign Creator ID"
          textToCopy={creatorId}
          variant="ghost"
          tooltipDelay={500}
          className="size-8 hover:bg-muted-foreground/10 text-muted-foreground opacity-0 group-hover/row:opacity-100"
        />
        <div className="relative">
          {threadId && (
            <Button
              className={`shrink-0 size-8 text-muted-foreground hover:bg-muted-foreground/10 ${
                selected ? "ring-3 ring-primary/30 border-primary" : ""
              }`}
              size="icon"
              variant="outline"
              title="View Creator's Inbox"
              disabled={inboxLoading}
              onClick={() => onSelectCreator(creatorId)}
            >
              <Inbox />
            </Button>
          )}
          {nextMessage && (
            <Pin
              variant="default"
              className="absolute -top-0.5 -right-0.5 size-2"
            />
          )}
        </div>
      </div>

      {threadId && (
        <Tooltip>
          <TooltipTrigger>
            <Pin variant={autopilot ? "success" : "destructive"} />
          </TooltipTrigger>
          <TooltipContent>
            {autopilot
              ? "Running on autopilot"
              : "Overridden thread (Amy will not reply)"}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

// Memoized on the creator object + the open-panel flag: the row's own data
// changes it, the table's UI state does not.
export default memo(UsernameColumnCell);
